// Vendor libraries
import { ElementRef } from '@angular/core';
import { FolderableType } from 'FoldersApp/enums/folderable-type.enum';
import { ToastMessageType } from 'NewAngular/features/components/toast-messages/toast-messages.enum';
// Directives
import { DragDropDirective } from 'NewAngular/features/directives/drag-drop/drag-drop.directive';
// Services
import { FolderableCollectionServiceBase } from 'FoldersApp/services/folderable-collection-service-base.service';
import { FolderOperationsService } from 'FoldersApp/services/folder-operations.service';
import { ToastMessagesService } from 'NewAngular/features/components/toast-messages/toast-messages.service';
import * as i0 from "@angular/core";
import * as i1 from "FoldersApp/services/folderable-collection-service-base.service";
import * as i2 from "FoldersApp/services/folder-operations.service";
import * as i3 from "NewAngular/features/components/toast-messages/toast-messages.service";
/**
 * Directive for an item in the folder item list.
 * Elements that use this directive can represent a file or a folder.
 * Used to display and manage drag-drop operations related to the item.
 */
export class FolderableDirective extends DragDropDirective {
    get isDraggable() {
        // TODO: remove this check when permissions block is implemented for serialized items.
        if (!this.folderable.item.permissions)
            return false;
        return this.folderable.item.permissions.canMove;
    }
    /**
     * Determine whether a folder item can be dropped to.
     *
     * Users can move items into folders if
     * - they own it.
     * - OR they have 'edit' permission for it.
     *
     * If an item is dropped onto a file displayed in the view root, we assume the user is trying to move the item
     * into the view root.
     */
    get isValidDropTarget() {
        // Cannot drop item onto itself
        if (this.isDragging)
            return false;
        if (this.folderable.resourceType === 'folder') {
            // Can drop folder item onto a folder that the user has edit permission for
            return this.folderable.item.permissions.canEdit && this.compatibleShareGroup;
        }
        // If dropping the item onto a file,
        // assume the user is trying to move the item into the folder that contains this file.
        // This allows moving items into a folder without scrolling to find the folder's name.
        if (this.ancestorInfo.id === null) {
            // Users can always move items in MyYCharts
            return true;
        }
        // We expect a folder to have a permissions block
        return this.ancestorInfo.permissions.canEdit && this.compatibleShareGroup;
    }
    constructor(elemRef, folderCollectionService, folderOperationsService, toastMessagesService) {
        super(elemRef);
        this.elemRef = elemRef;
        this.folderCollectionService = folderCollectionService;
        this.folderOperationsService = folderOperationsService;
        this.toastMessagesService = toastMessagesService;
        this.compatibleShareGroup = true;
    }
    dropEventHandler(event) {
        const { id, itemType, parentFolderId } = this.getDragEventData(event);
        if (id === null || itemType === null)
            return;
        // If the user is dropping the item on a folder, attempt to move the item into the folder.
        // If the user is dropping the item on a file,
        // assume they are trying to move an item into the file's parent folder.
        const targetFolderId = this.folderable.resourceType === FolderableType.FOLDER ? this.folderable.item.id : this.ancestorInfo.id;
        // Do not attempt to move if the item is already in the target folder
        if (parentFolderId === targetFolderId)
            return;
        this.folderOperationsService.moveFolderableItem(itemType, id, targetFolderId).subscribe({
            next: (response) => {
                this.toastMessagesService.addMessage(response.message);
                this.folderCollectionService.refreshData();
            },
            error: (response) => {
                this.toastMessagesService.addMessage(response.error.error_message, ToastMessageType.ERROR);
            },
        });
    }
    drop(event) {
        const elem = this.getDropTarget(event);
        elem.style.borderTop = '';
        super.drop(event);
    }
    dragenter(event) {
        const elem = this.getDropTarget(event);
        this.validateCompatibleShareGroups(event);
        super.dragenter(event);
        this.addFileDropAreaStyles(elem);
        this.addInvalidDraggableStyles(event, elem);
    }
    dragover(event) {
        const elem = this.getDropTarget(event);
        super.dragover(event);
        this.addFileDropAreaStyles(elem);
        this.addInvalidDraggableStyles(event, elem);
    }
    dragend(event) {
        const elem = this.getDropTarget(event);
        elem.style.borderTop = '';
        super.dragend(event);
        sessionStorage.removeItem('draggedItemShareGroup');
        sessionStorage.removeItem('publicShareGroup');
    }
    dragleave(event) {
        const elem = this.getDropTarget(event);
        elem.style.borderTop = '';
        super.dragleave(event);
    }
    setEventData(event) {
        // eslint-disable-next-line no-param-reassign
        event.dataTransfer.effectAllowed = 'move';
        // Drag data must be set in the dragstart event; cannot be set in other events
        event.dataTransfer.setData('id', `${this.folderable.item.id}`);
        event.dataTransfer.setData('itemType', `${this.folderable.resourceType}`);
        event.dataTransfer.setData('parentFolderId', `${this.ancestorInfo.id}`);
        event.dataTransfer.setData('shareGroup', `${this.folderable.item.sharing.client_group_id}`);
        // Share group is added to sessionStorage because dataTransfer is not available for access on any event but
        // `dragstart` and `drop`
        sessionStorage.setItem('draggedItemShareGroup', `${this.folderable.item.sharing.client_group_id}`);
        sessionStorage.setItem('publicShareGroup', this.folderable.item.sharing.is_client_group_public
            ? this.folderable.item.sharing.is_client_group_public.toString()
            : 'false');
    }
    // TODO: Add a class for the border top to simplify these blocks of code
    addFileDropAreaStyles(elem) {
        if (this.isValidDropTarget && this.folderable.resourceType !== FolderableType.FOLDER) {
            // eslint-disable-next-line no-param-reassign
            elem.style.borderTop = 'solid 3pt #0282e8';
            elem.classList.remove(this.positiveHighlightClass);
        }
    }
    addInvalidDraggableStyles(event, elem) {
        const { effectAllowed } = this.getDragEventData(event);
        if (effectAllowed !== 'move') {
            if (this.folderable.resourceType !== FolderableType.FOLDER) {
                // eslint-disable-next-line no-param-reassign
                elem.style.borderTop = 'solid 3pt #f8aaaa';
            }
            else {
                elem.classList.add(this.negativeHighlightClass);
                elem.classList.remove(this.positiveHighlightClass);
            }
        }
    }
    getDragEventData(event) {
        let id = parseInt(event.dataTransfer.getData('id'), 10);
        let parentFolderId = parseInt(event.dataTransfer.getData('parentFolderId'), 10);
        let currentShareGroup = parseInt(event.dataTransfer.getData('shareGroup') || sessionStorage.getItem('draggedItemShareGroup'), 10);
        const publicShareGroup = sessionStorage.getItem('publicShareGroup') === 'true';
        const itemType = event.dataTransfer.getData('itemType') || null;
        const { effectAllowed } = event.dataTransfer;
        if (Number.isNaN(id))
            id = null;
        if (Number.isNaN(parentFolderId))
            parentFolderId = null;
        if (Number.isNaN(currentShareGroup))
            currentShareGroup = null;
        return { id, itemType, effectAllowed, currentShareGroup, parentFolderId, publicShareGroup };
    }
    validateCompatibleShareGroups(event) {
        const { currentShareGroup, publicShareGroup } = this.getDragEventData(event);
        this.compatibleShareGroup =
            !currentShareGroup ||
                !this.folderable.item.sharing ||
                this.folderable.item.sharing.ancestor_client_group_ids.length === 0 ||
                this.folderable.item.sharing.ancestor_client_group_ids.includes(currentShareGroup) ||
                publicShareGroup;
    }
}
FolderableDirective.ɵfac = function FolderableDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || FolderableDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.FolderableCollectionServiceBase), i0.ɵɵdirectiveInject(i2.FolderOperationsService), i0.ɵɵdirectiveInject(i3.ToastMessagesService)); };
FolderableDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: FolderableDirective, selectors: [["", "ycn-folderable", ""]], inputs: { folderable: "folderable", ancestorInfo: "ancestorInfo" }, standalone: false, features: [i0.ɵɵInheritDefinitionFeature] });
